import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  PharmacySearchPayload,
  WholesalerPharmacySearchResult,
  WholesalerSearchPayload,
} from '../models';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { CustomB2BUnit, CustomUser } from 'src/app/shared/model';
import { HttpClient } from '@angular/common/http';
import { getLastValueSync, OccEndpointsService } from '@spartacus/core';
import { CustomRegistrationService } from 'src/app/custom-user-registration/custom-registration.service';
import { CustomAddToCartService } from 'src/app/custom-add-to-cart/custom-add-to-cart.service';

@Injectable({
  providedIn: 'root',
})
export class WholesalerPharmacyService {
  constructor(
    protected user: UserAccountFacade,
    protected http: HttpClient,
    protected occEndpointsService: OccEndpointsService,
    protected registrationService: CustomRegistrationService,
    protected addToCartService: CustomAddToCartService
  ) {}
  selected$ = new BehaviorSubject<CustomB2BUnit>(null);
  searchResults$ = new BehaviorSubject<CustomB2BUnit[]>(null);
  userDefaultSelection$ = new BehaviorSubject<{
    [userId: string]: CustomB2BUnit;
  }>(null);
  userDefaultWholesalerPharmacyId$ = this.user.get().pipe(
    switchMap((user: CustomUser) => {
      const cachedValue = this.userDefaultSelection$.getValue()?.[user?.uid];
      if (cachedValue) {
        return of(cachedValue);
      }
      const defaultId = user?.pharmacyId || user?.wholesalerId;
      if (defaultId) {
        return this.registrationService
          .getAccountData(user.uid, defaultId)
          .pipe(
            tap((data) =>
              this.userDefaultSelection$.next({
                [user.uid]: data,
              })
            )
          );
      }
      return of(null);
    })
  );

  setDefaultWholesaler(selected: CustomB2BUnit): void {
    const url = this.occEndpointsService.buildUrl('setWholesaler');
    this.http
      .patch(url, {
        chosenB2BUnitUid: selected.uid,
      })
      .subscribe(() => {
        this.selected$.next(selected);
      });
  }

  setDefaultPharmacy(selected: CustomB2BUnit): void {
    const userId = getLastValueSync(this.user.get()).uid;
    const url = this.occEndpointsService.buildUrl('setPharmacy', {
      urlParams: {
        userId,
      },
    });
    this.http
      .put(url, {
        chosenB2BUnitUid: selected.uid,
      })
      .subscribe(() => {
        this.selected$.next(selected);
      });
  }

  getDefaultWholesalerPharmacy(): Observable<CustomB2BUnit> {
    return combineLatest([
      this.userDefaultWholesalerPharmacyId$,
      this.selected$,
    ]).pipe(
      map(([defaultId, selectedId]) => {
        return selectedId || defaultId;
      })
    );
  }

  resetSearchResults() {
    this.searchResults$.next(null);
  }

  searchWholesalers(payload: WholesalerSearchPayload): void {
    const url = this.occEndpointsService.buildUrl('getWholesalers', {
      queryParams: {
        queryText: payload.value,
      },
    });
    this.http.get<CustomB2BUnit[]>(url).subscribe((data) => {
      this.searchResults$.next(data);
    });
  }

  searchPharmacies(payload: PharmacySearchPayload): void {
    const userId = getLastValueSync(this.user.get()).uid;
    const url = this.occEndpointsService.buildUrl('getPharmacies', {
      urlParams: {
        userId,
      },
      queryParams: {
        name: payload.name,
        city: payload.city,
      },
    });
    this.http.get<WholesalerPharmacySearchResult>(url).subscribe((data) => {
      this.searchResults$.next(data.b2bUnitData);
    });
  }
}
